/**
 * DVL Phase 4 — Financial Health Ribbon
 *
 * A drop-in component that calls /api/financial/ribbon and shows
 * a discreet but persistent banner at the top of any financial page.
 *
 * Usage:
 *   import { FinancialHealthRibbon } from './FinancialHealthRibbon';
 *   <FinancialHealthRibbon /> at the top of any financial page.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../App';
import {
  CheckCircle2, AlertTriangle, AlertOctagon, Loader2, Sparkles, ArrowLeft,
} from 'lucide-react';

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-50',  border: 'border-emerald-300', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  amber:   { bg: 'bg-amber-50',    border: 'border-amber-300',   text: 'text-amber-900',   dot: 'bg-amber-500' },
  rose:    { bg: 'bg-rose-50',     border: 'border-rose-300',    text: 'text-rose-900',    dot: 'bg-rose-500' },
  lime:    { bg: 'bg-lime-50',     border: 'border-lime-300',    text: 'text-lime-800',    dot: 'bg-lime-500' },
  orange:  { bg: 'bg-orange-50',   border: 'border-orange-300',  text: 'text-orange-900',  dot: 'bg-orange-500' },
  grey:    { bg: 'bg-gray-50',     border: 'border-gray-300',    text: 'text-gray-700',    dot: 'bg-gray-400' },
};

const Icon = ({ level }) => {
  if (level === 'healthy') return <CheckCircle2 className="w-5 h-5" />;
  if (level === 'drift_resolved') return <AlertTriangle className="w-5 h-5" />;
  if (level === 'attention') return <AlertOctagon className="w-5 h-5" />;
  return <Loader2 className="w-5 h-5 animate-spin" />;
};

export const FinancialHealthRibbon = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const r = await axiosInstance.get('/financial/ribbon');
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'فشل تحميل الحالة المالية');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    // refresh every 5 min
    const t = setInterval(fetch, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (loading) {
    return (
      <div
        data-testid="financial-health-ribbon-loading"
        className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-2 text-xs text-gray-500"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> جاري فحص الحالة المالية…
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500">
        تعذّر جلب الحالة المالية: {err || 'unknown'}
      </div>
    );
  }
  const ribbon = data.ribbon || {};
  const conf = data.confidence || {};
  const palette = COLOR_MAP[ribbon.color] || COLOR_MAP.grey;
  const confPalette = COLOR_MAP[conf.color] || COLOR_MAP.grey;

  if (compact) {
    return (
      <div
        data-testid="financial-health-ribbon-compact"
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${palette.bg} ${palette.border} border ${palette.text}`}
        title={ribbon.title_en}
      >
        <span className={`inline-block w-2 h-2 rounded-full ${palette.dot}`} />
        <span className="text-xs font-bold">{ribbon.symbol} {ribbon.title_ar}</span>
        <span className="text-[10px] text-gray-500">·</span>
        <span className={`text-[10px] font-bold ${confPalette.text}`}>
          {conf.score?.toFixed(0)}/100 ({conf.grade})
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="financial-health-ribbon"
      className={`${palette.bg} ${palette.border} border rounded-xl px-5 py-3 flex items-center gap-4 flex-wrap`}
    >
      <div className={`w-9 h-9 rounded-lg ${palette.dot} bg-opacity-15 flex items-center justify-center ${palette.text}`}>
        <Icon level={ribbon.level} />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className={`text-sm font-bold ${palette.text}`} data-testid="ribbon-title">
          {ribbon.symbol} {ribbon.title_ar}
        </div>
        <div className="text-[11px] text-gray-600 mt-0.5" data-testid="ribbon-subtitle">
          {ribbon.subtitle_ar}
        </div>
      </div>
      <div className="text-center px-4 border-l border-gray-200">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Confidence</div>
        <div className={`text-2xl font-mono font-bold ${confPalette.text}`} data-testid="confidence-score">
          {conf.score?.toFixed(0)}
          <span className="text-xs">/100</span>
        </div>
        <div className={`text-[10px] font-bold ${confPalette.text}`}>Grade {conf.grade}</div>
      </div>
      {ribbon.level !== 'healthy' && (
        <Link
          to="/dvl-financial-dashboard"
          data-testid="ribbon-drill-link"
          className={`text-xs font-bold flex items-center gap-1 ${palette.text} hover:opacity-70`}
        >
          <Sparkles className="w-3.5 h-3.5" /> تفاصيل الفجوات <ArrowLeft className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
};

export default FinancialHealthRibbon;
